import { useState, type CSSProperties } from 'react';
import { supabase } from '@/lib/supabase';

// Strong password: 10+ chars with upper, lower, number, symbol.
function passwordProblems(pw: string): string[] {
  const p: string[] = [];
  if (pw.length < 10) p.push('at least 10 characters');
  if (!/[A-Z]/.test(pw)) p.push('an uppercase letter');
  if (!/[a-z]/.test(pw)) p.push('a lowercase letter');
  if (!/[0-9]/.test(pw)) p.push('a number');
  if (!/[^A-Za-z0-9]/.test(pw)) p.push('a symbol');
  return p;
}

/**
 * ChangePasswordModal — lets a logged-in user change their own password via the
 * `admin-users` Edge Function (no email, no rate limit).
 *
 * - Clicking the background does NOT close it (only × or Cancel), so you can't
 *   lose what you typed by mis-clicking.
 * - Checks for a live session first, so an expired login gives a clear message.
 */

export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit() {
    setError(null);
    if (!currentPw || !newPw || !confirmPw) return setError('Please fill in all three fields.');
    const problems = passwordProblems(newPw);
    if (problems.length) return setError('Your new password needs ' + problems.join(', ') + '.');
    if (newPw !== confirmPw) return setError('New password and confirmation do not match.');
    if (newPw === currentPw) return setError('New password must be different from your current password.');

    setBusy(true);
    try {
      // Make sure we have a valid (auto-refreshed) session before calling the
      // function — otherwise an expired token surfaces as a vague error.
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session) {
        setError('Your session has expired. Please sign out and sign in again, then retry.');
        return;
      }

      const { data, error: fnErr } = await supabase.functions.invoke('admin-users', {
        body: { action: 'set_own_password', currentPassword: currentPw, newPassword: newPw },
      });
      if (fnErr) {
        let msg = fnErr.message;
        try {
          const j = await (fnErr as any).context?.json?.();
          if (j?.error) msg = j.error;
        } catch { /* ignore */ }
        setError(msg);
        return;
      }
      if ((data as any)?.error) {
        setError((data as any).error);
        return;
      }
      setDone(true);
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay}>
      <div style={cardStyle}>
        <div style={header}>
          <h2 style={title}>Change Password</h2>
          <button type="button" aria-label="Close" style={closeBtn} onClick={onClose}>×</button>
        </div>

        {done ? (
          <div style={{ padding: '8px 0' }}>
            <p style={{ margin: '0 0 18px', lineHeight: 1.5 }}>
              ✅ Your password has been updated. It takes effect immediately — your
              current session stays signed in.
            </p>
            <button type="button" style={primaryBtn} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <Field label="Current password" value={currentPw} onChange={setCurrentPw} show={show} autoFocus />
            <Field label="New password" value={newPw} onChange={setNewPw} show={show} />
            <Field label="Confirm new password" value={confirmPw} onChange={setConfirmPw} show={show} onEnter={handleSubmit} />

            <label style={showRow}>
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
              <span>Show passwords</span>
            </label>

            <p style={hint}>Use a strong password: 10+ characters with upper- &amp; lower-case, a number, and a symbol.</p>
            {error && <div style={errorBox}>{error}</div>}

            <div style={actions}>
              <button type="button" style={secondaryBtn} onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" style={primaryBtn} onClick={handleSubmit} disabled={busy}>
                {busy ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, show, autoFocus, onEnter,
}: {
  label: string; value: string; onChange: (v: string) => void; show: boolean; autoFocus?: boolean; onEnter?: () => void;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={fieldLabel}>{label}</label>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        autoFocus={autoFocus}
        autoComplete={label === 'Current password' ? 'current-password' : 'new-password'}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }}
        style={input}
      />
    </div>
  );
}

const overlay: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const cardStyle: CSSProperties = { background: 'var(--card-bg, #fff)', color: 'var(--text, #0f172a)', width: '100%', maxWidth: 420, borderRadius: 12, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const header: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 };
const title: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 700 };
const closeBtn: CSSProperties = { border: 'none', background: 'transparent', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: 'var(--muted, #64748b)' };
const fieldLabel: CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--muted, #475569)' };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border, #cbd5e1)', fontSize: 14, outline: 'none' };
const showRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted, #475569)', marginBottom: 6, cursor: 'pointer' };
const hint: CSSProperties = { margin: '0 0 14px', fontSize: 12, color: 'var(--muted, #94a3b8)' };
const errorBox: CSSProperties = { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 14 };
const actions: CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 10 };
const primaryBtn: CSSProperties = { background: 'var(--accent, #2563eb)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const secondaryBtn: CSSProperties = { background: 'transparent', color: 'var(--muted, #475569)', border: '1px solid var(--border, #cbd5e1)', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
